import { useState } from 'react'
import { MoreHorizontal, Package, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ProductDeleteDialog } from '@/features/product-catalog/components/ProductDeleteDialog'
import {
  useListV2Products,
  type V2ProductListItem,
} from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { V2CatalogListEmpty } from '../components/V2CatalogListEmpty'
import { V2CatalogPageHeader } from '../components/V2CatalogPageHeader'
import { V2CatalogTableFrame } from '../components/V2CatalogTableFrame'
import { V2TableLink } from '../components/V2TableLink'
import { V2CreateProductDialog } from '../components/V2CreateProductDialog'
import { getV2ProductDetailPath } from '../utils/routes'

const PRODUCT_TABLE_COLUMNS = [
  { width: '45%' },
  { width: '35%' },
  { width: '20%' },
] as const

function ProductsTableColGroup() {
  return (
    <colgroup>
      {PRODUCT_TABLE_COLUMNS.map((column, index) => (
        <col key={index} style={{ width: column.width }} />
      ))}
    </colgroup>
  )
}

function getProductTitle(
  product: V2ProductListItem,
  fallbackTitle: string
): string {
  return product.name?.trim() || fallbackTitle
}

export default function V2ProductsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('common')
  const { data: products = [], isSuccess, isLoading, isError } =
    useListV2Products()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [productToDelete, setProductToDelete] =
    useState<V2ProductListItem | null>(null)

  const hasProducts = isSuccess && products.length > 0
  const showEmptyState = !isLoading && isSuccess && products.length === 0
  const fallbackTitle = t('v2.pages.products.fallbackTitle')
  const deleteTarget = productToDelete
  const showDeleteDialog = deleteTarget !== null

  function handleProductCreated(productId: string) {
    navigate(getV2ProductDetailPath(productId))
  }

  return (
    <>
      <div className="space-y-4">
        <V2CatalogPageHeader
          title={t('v2.pages.products.title')}
          subtitle={t('v2.pages.products.subtitle')}
          actions={
            <Button
              className="gap-2"
              onClick={() => setCreateDialogOpen(true)}
              type="button"
            >
              <Plus className="size-4" aria-hidden />
              {t('v2.pages.products.addButton')}
            </Button>
          }
        />

        {(isLoading || hasProducts) && (
          <V2CatalogTableFrame
            isLoading={isLoading}
            skeletonColumnCount={PRODUCT_TABLE_COLUMNS.length}
          >
            {hasProducts ? (
            <table className="w-full min-w-full table-fixed caption-bottom text-sm">
              <ProductsTableColGroup />
              <TableHeader>
                <TableRow className="group/row">
                  <TableHead className="sticky top-0 z-10 bg-background group-hover/row:bg-muted">
                    {t('v2.pages.products.columns.name')}
                  </TableHead>
                  <TableHead className="sticky top-0 z-10 bg-background group-hover/row:bg-muted">
                    {t('v2.pages.products.columns.sku')}
                  </TableHead>
                  <TableHead className="sticky top-0 z-10 bg-background text-end group-hover/row:bg-muted">
                    {t('v2.pages.products.columns.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => {
                  const title = getProductTitle(product, fallbackTitle)
                  const skuLabel =
                    product.sku != null && product.sku.trim() !== ''
                      ? product.sku.trim()
                      : t('notAvailable')

                  return (
                    <TableRow key={product.id} className="group/row">
                      <TableCell className="max-w-0 truncate overflow-hidden bg-background group-hover/row:bg-muted">
                        <V2TableLink
                          to={getV2ProductDetailPath(product.id)}
                          truncate
                          title={title}
                        >
                          {title}
                        </V2TableLink>
                      </TableCell>
                      <TableCell className="max-w-0 truncate overflow-hidden bg-background text-muted-foreground group-hover/row:bg-muted">
                        <span className="block truncate" title={skuLabel}>
                          {skuLabel}
                        </span>
                      </TableCell>
                      <TableCell className="bg-background text-end group-hover/row:bg-muted">
                        <div onClick={(event) => event.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-8 shrink-0"
                                aria-label={t(
                                  'v2.pages.products.actionsMenuAriaLabel',
                                  { title }
                                )}
                              >
                                <MoreHorizontal className="size-4" aria-hidden />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                variant="destructive"
                                onClick={() => setProductToDelete(product)}
                              >
                                <Trash2 className="size-4" aria-hidden />
                                {t('actions.delete')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </table>
            ) : null}
          </V2CatalogTableFrame>
        )}

        {showEmptyState && (
          <V2CatalogListEmpty
            icon={<Package aria-hidden />}
            title={t('v2.pages.products.empty.title')}
            description={t('v2.pages.products.empty.description')}
            action={
              <Button
                className="gap-2"
                onClick={() => setCreateDialogOpen(true)}
                type="button"
              >
                <Plus className="size-4" aria-hidden />
                {t('v2.pages.products.addButton')}
              </Button>
            }
          />
        )}

        {isError && (
          <p className="text-sm text-destructive">
            {t('notifications.unknownError')}
          </p>
        )}
      </div>

      <V2CreateProductDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleProductCreated}
      />

      {showDeleteDialog && (
        <ProductDeleteDialog
          linkedChatCount={0}
          open={showDeleteDialog}
          onOpenChange={(open) => {
            if (!open) {
              setProductToDelete(null)
            }
          }}
          product={deleteTarget}
          onDeleted={() => setProductToDelete(null)}
        />
      )}
    </>
  )
}
